/**
 * DG Basics — comportamento da página de produto (carregado só no template de produto).
 *
 * 1. Tamanho não vem pré-selecionado: o cliente precisa escolher. Enquanto faltar,
 *    "Comprar" (principal e barra fixa) leva até o seletor e avisa o que falta.
 * 2. Blocos DG com [data-dg-live] (preço, parcelas) se atualizam ao trocar a variante.
 * 3. Janela do guia de tamanhos e vídeos com capa clicável.
 *
 * O envio ao carrinho continua sendo o formulário original do tema (integração Yampi intacta).
 */

const SIZE_KEYS = ['size', 'tamanho', 'tam'];
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @returns {HTMLElement | null} */
function mainPicker() {
  return document.querySelector('.product-information variant-picker[data-template-product-match="true"]');
}

/** Fieldsets que o cliente precisa escolher (tamanho). */
function requiredFieldsets() {
  const picker = mainPicker();
  if (!picker) return [];
  return Array.from(picker.querySelectorAll('fieldset[data-dg-option]')).filter((fs) =>
    SIZE_KEYS.some((key) => (fs.getAttribute('data-dg-option') || '').includes(key))
  );
}

/** Nomes das opções já escolhidas pelo cliente nesta visita. */
const chosen = new Set();

function optionName(fieldset) {
  return fieldset.querySelector('input')?.dataset.optionName || '';
}

function optionLabel(fieldset) {
  const legend = fieldset.querySelector('legend');
  const text = legend?.childNodes[0]?.textContent?.trim();
  return (text || 'opção').toLowerCase();
}

function missingFieldsets() {
  return requiredFieldsets().filter((fs) => !chosen.has(optionName(fs)));
}

/** Tira a seleção automática do tamanho enquanto o cliente não escolher. */
function clearAutoSelection() {
  for (const fs of missingFieldsets()) {
    for (const input of fs.querySelectorAll('input:checked')) {
      input.checked = false;
      input.dataset.currentChecked = 'false';
    }
    fs.classList.add('dg-choice-pending');
  }
}

function clearMissingState(fieldset) {
  fieldset.classList.remove('dg-choice-pending', 'dg-choice-missing');
  fieldset.querySelector('.dg-choice-msg')?.remove();
}

function showMissing() {
  const missing = missingFieldsets();
  if (!missing.length) return false;
  const first = missing[0];

  for (const fs of missing) {
    fs.classList.add('dg-choice-missing');
    if (!fs.querySelector('.dg-choice-msg')) {
      const msg = document.createElement('p');
      msg.className = 'dg-choice-msg';
      msg.setAttribute('role', 'alert');
      msg.textContent = `Escolha o ${optionLabel(fs)} para continuar.`;
      fs.querySelector('legend')?.insertAdjacentElement('afterend', msg);
    }
  }

  first.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  const target = first.querySelector('input:not([aria-disabled="true"])') || first.querySelector('input');
  window.setTimeout(() => target?.focus({ preventScroll: true }), reduceMotion ? 0 : 350);
  return true;
}

/* ---------- Guarda do "Comprar" enquanto faltar opção ---------- */

function isMainBuyTrigger(target) {
  if (!(target instanceof Element)) return false;
  if (target.closest('.sticky-add-to-cart__button')) return true;
  const button = target.closest('.add-to-cart-button, button[name="add"]');
  return Boolean(button && button.closest('.product-information') && !button.closest('dialog, product-card'));
}

document.addEventListener(
  'click',
  (event) => {
    if (!isMainBuyTrigger(event.target) || !missingFieldsets().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showMissing();
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('.product-information') || form.closest('dialog, product-card')) return;
    if (!form.querySelector('[name="id"]') || !missingFieldsets().length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showMissing();
  },
  true
);

/* ---------- Escolha do cliente ---------- */

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const fieldset = input.closest('fieldset[data-dg-option]');
  if (!fieldset || !requiredFieldsets().includes(fieldset)) return;
  chosen.add(input.dataset.optionName || '');
  clearMissingState(fieldset);
});

/* ---------- Atualização ao trocar variante ---------- */

function syncLiveBlocks(html) {
  for (const el of document.querySelectorAll('.product-information [data-dg-live]')) {
    const fresh = html.querySelector(`[data-dg-live="${el.getAttribute('data-dg-live')}"]`);
    if (!fresh || fresh.innerHTML === el.innerHTML) continue;
    el.innerHTML = fresh.innerHTML;
    // Transição discreta para mostrar que o dado mudou
    if (!reduceMotion) {
      el.classList.remove('dg-live-updated');
      void el.offsetWidth;
      el.classList.add('dg-live-updated');
    }
  }
}

/** Lista de cores com rolagem: deixa a cor marcada visível sem mexer na rolagem da página. */
function revealCheckedColor() {
  for (const list of document.querySelectorAll('.product-information .dg-opt__list')) {
    const label = list.querySelector('input:checked')?.closest('label');
    if (!label || list.scrollWidth <= list.clientWidth) continue;
    const item = label.getBoundingClientRect();
    const box = list.getBoundingClientRect();
    if (item.left < box.left) list.scrollLeft += item.left - box.left - 6;
    else if (item.right > box.right) list.scrollLeft += item.right - box.right + 6;
  }
}

function afterVariantUpdate(detail) {
  if (detail?.html) syncLiveBlocks(detail.html);
  // O tema redesenha o seletor com um tamanho marcado; mantém em branco até o cliente escolher.
  clearAutoSelection();
  revealCheckedColor();
  // Avisa outras seções da página (card "O que você recebe") que a seleção mudou.
  document.dispatchEvent(new CustomEvent('dg:selection'));
}

import('@shopify/events')
  .then(({ StandardEvents }) => {
    const section = document.querySelector('.product-information')?.closest('.shopify-section');
    if (!section || !StandardEvents?.productSelect) return;
    section.addEventListener(StandardEvents.productSelect, (event) => {
      if (event.target instanceof Element && event.target.closest('product-card, dialog')) return;
      event.promise?.then(({ detail }) => afterVariantUpdate(detail)).catch(() => {});
    });
  })
  .catch(() => {});

/* ---------- Janelas DG (guia de tamanhos e outras com data-dg-dialog) ---------- */

const DG_DIALOGS = 'dialog.dg-sguide__dialog, dialog[data-dg-dialog]';

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-dg-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-dg-dialog-open') || '');
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      dialog.showModal();
      dialog._dgOpener = opener;
    }
    return;
  }

  const closer = target.closest('[data-dg-dialog-close]');
  const dialog = target.closest(DG_DIALOGS);
  if (closer && dialog instanceof HTMLDialogElement) {
    dialog.close();
    return;
  }
  // Clique fora do conteúdo (no fundo escuro) fecha a janela.
  if (target instanceof HTMLDialogElement && target.matches(DG_DIALOGS)) {
    const r = target.getBoundingClientRect();
    const { clientX: x, clientY: y } = /** @type {MouseEvent} */ (event);
    if (x < r.left || x > r.right || y < r.top || y > r.bottom) target.close();
  }
});

document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog._dgOpener) dialog._dgOpener.focus();
  },
  true
);

/* ---------- Vídeos com capa ---------- */

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-dg-video-play]') : null;
  if (!button) return;
  const item = button.closest('.dg-pvideos__item');
  const video = item?.querySelector('video');
  if (!video) return;
  for (const other of document.querySelectorAll('.dg-pvideos__item.is-playing video')) {
    if (other !== video) other.pause();
  }
  video.controls = true;
  item.classList.add('is-playing');
  video.play().catch(() => {});
  video.focus({ preventScroll: true });
});

/* ---------- Stories do produto ---------- */

/**
 * Leva o cliente de volta à escolha de cor/tamanho e compra, sem adicionar nada ao carrinho.
 * O que já foi escolhido continua marcado; se faltar o tamanho, mostra o aviso no campo.
 */
function goToPurchase() {
  if (showMissing()) return;
  const picker = mainPicker();
  const buy = document.querySelector('.product-information .buy-buttons-block');
  (picker || buy)?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  const button = buy?.querySelector('.add-to-cart-button');
  window.setTimeout(() => button?.focus({ preventScroll: true }), reduceMotion ? 0 : 450);
}

/* Botões "Comprar" fora da área de compra (ex.: seção DG · Encerramento) */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-dg-go-buy]') : null;
  if (!target) return;
  event.preventDefault();
  goToPurchase();
});

/** Trava a rolagem da página (o tema rola em .page-wrapper no desktop). */
const scrollLock = {
  saved: /** @type {{ win: number, wrap: number } | null} */ (null),
  lock() {
    const wrap = document.querySelector('.page-wrapper');
    this.saved = { win: window.scrollY, wrap: wrap?.scrollTop ?? 0 };
    document.documentElement.setAttribute('scroll-lock', '');
  },
  unlock() {
    document.documentElement.removeAttribute('scroll-lock');
    if (!this.saved) return;
    const wrap = document.querySelector('.page-wrapper');
    if (wrap) wrap.scrollTop = this.saved.wrap;
    window.scrollTo(0, this.saved.win);
    this.saved = null;
  },
};

function currentPriceText() {
  const el =
    document.querySelector('.product-information [data-dg-live^="price-"] .dg-pprice__now') ||
    document.querySelector('.product-information product-price [ref="priceContainer"]');
  return el?.textContent?.trim().replace(/\s+/g, ' ') || '';
}

function initStories(root) {
  const dialog = root.querySelector('[data-dg-story-viewer]');
  const dataEl = root.querySelector('[data-dg-stories-data]');
  if (!(dialog instanceof HTMLDialogElement) || !dataEl) return;

  /** @type {{ src: string, type: string, poster: string | null, title: string }[]} */
  let stories = [];
  try {
    stories = JSON.parse(dataEl.textContent || '[]').filter((s) => s && s.src);
  } catch {
    return;
  }
  if (!stories.length) {
    root.hidden = true;
    return;
  }

  const video = /** @type {HTMLVideoElement} */ (dialog.querySelector('[data-dg-story-video]'));
  const progress = dialog.querySelector('[data-dg-story-progress]');
  const title = dialog.querySelector('[data-dg-story-title]');
  const toggle = dialog.querySelector('[data-dg-story-toggle]');
  const mute = dialog.querySelector('[data-dg-story-mute]');
  const bigPlay = dialog.querySelector('[data-dg-story-bigplay]');
  const prev = dialog.querySelector('[data-dg-story-prev]');
  const next = dialog.querySelector('[data-dg-story-next]');
  const price = dialog.querySelector('[data-dg-story-price]');

  let index = 0;
  let muted = true;
  let opener = /** @type {HTMLElement | null} */ (null);
  let raf = 0;
  let afterClose = /** @type {(() => void) | null} */ (null);

  const segs = stories.map(() => {
    const seg = document.createElement('span');
    seg.className = 'dg-sv__seg';
    progress?.append(seg);
    return seg;
  });

  function paintProgress() {
    segs.forEach((seg, i) => {
      let p = 0;
      if (i < index) p = 1;
      else if (i === index && video.duration) p = Math.min(1, video.currentTime / video.duration);
      seg.style.setProperty('--p', String(p));
    });
  }

  function loop() {
    paintProgress();
    raf = requestAnimationFrame(loop);
  }

  function setPaused(paused) {
    dialog.classList.toggle('is-paused', paused);
    toggle?.setAttribute('aria-label', paused ? 'Reproduzir' : 'Pausar');
    if (bigPlay) bigPlay.hidden = !paused;
  }

  function setMuted(value) {
    muted = value;
    video.muted = value;
    dialog.classList.toggle('is-muted', value);
    mute?.setAttribute('aria-label', value ? 'Ativar som' : 'Desativar som');
  }

  function play() {
    const attempt = video.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(() => setPaused(false)).catch(() => setPaused(true));
    } else {
      setPaused(false);
    }
  }

  function load(i) {
    index = Math.max(0, Math.min(stories.length - 1, i));
    const story = stories[index];
    video.pause();
    if (story.poster) video.poster = story.poster;
    else video.removeAttribute('poster');
    video.src = story.src;
    video.muted = muted;
    video.load();
    if (title) title.textContent = story.title;
    if (prev instanceof HTMLButtonElement) prev.disabled = index === 0;
    if (next instanceof HTMLButtonElement) next.disabled = index === stories.length - 1;
    paintProgress();
    play();
  }

  function open(i, trigger) {
    opener = trigger;
    if (price) price.textContent = currentPriceText() || price.textContent;
    scrollLock.lock();
    dialog.showModal();
    setMuted(true);
    load(i);
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(loop);
    // Foco inicial no botão de pausar (controle principal)
    if (toggle instanceof HTMLElement) toggle.focus();
  }

  function close(then) {
    afterClose = then || null;
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => {
    cancelAnimationFrame(raf);
    video.pause();
    video.removeAttribute('src');
    video.load();
    setPaused(false);
    scrollLock.unlock();
    const callback = afterClose;
    afterClose = null;
    if (callback) callback();
    else opener?.focus({ preventScroll: true });
  });

  video.addEventListener('ended', () => {
    if (index < stories.length - 1) load(index + 1);
    else {
      paintProgress();
      setPaused(true); // último vídeo: para, sem repetir
    }
  });
  video.addEventListener('pause', () => dialog.open && setPaused(true));
  video.addEventListener('play', () => setPaused(false));

  function togglePlay() {
    if (video.paused) {
      if (video.ended) video.currentTime = 0;
      play();
    } else {
      video.pause();
    }
  }

  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumb = target.closest('[data-dg-story-open]');
    if (thumb instanceof HTMLElement) {
      open(Number(thumb.dataset.dgStoryOpen) || 0, thumb);
      return;
    }
    if (target.closest('[data-dg-story-close]')) return close();
    if (target.closest('[data-dg-story-toggle], [data-dg-story-bigplay]')) return togglePlay();
    if (target.closest('[data-dg-story-mute]')) {
      setMuted(!muted);
      if (!muted && video.paused) play();
      return;
    }
    if (target.closest('[data-dg-story-prev]')) return load(index - 1);
    if (target.closest('[data-dg-story-next]')) return load(index + 1);
    if (target.closest('[data-dg-story-buy]')) return close(goToPurchase);
    if (target === video) return togglePlay();
    // Clique no fundo escuro fecha
    if (target === dialog || target.classList.contains('dg-sv__stage')) close();
  });

  dialog.addEventListener('keydown', (event) => {
    const onButton = event.target instanceof HTMLButtonElement;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      load(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      load(index - 1);
    } else if ((event.key === ' ' || event.key === 'k') && !onButton) {
      event.preventDefault();
      togglePlay();
    } else if (event.key === 'm') {
      setMuted(!muted);
    }
  });
}

/* ---------- Início ---------- */

/* ---------- Barra fixa de compra: reserva o espaço dela no fim da página ---------- */

function watchStickyBar() {
  const bar = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar');
  if (!bar || bar.dataset.dgWatched) return;
  bar.dataset.dgWatched = 'true';
  const update = () => {
    const bottom = parseFloat(getComputedStyle(bar).bottom) || 0;
    document.documentElement.style.setProperty('--dg-sticky-space', `${Math.ceil(bar.offsetHeight + bottom)}px`);
  };
  new ResizeObserver(update).observe(bar);
  update();
}

function init() {
  clearAutoSelection();
  revealCheckedColor();
  watchStickyBar();
  document.querySelectorAll('[data-dg-stories]').forEach(initStories);
}

// Editor de temas: a seção é redesenhada ao editar; reativa os stories.
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target instanceof Element ? event.target : null;
  section?.querySelectorAll('[data-dg-stories]').forEach(initStories);
  clearAutoSelection();
  watchStickyBar();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
